import { useState } from "react";
import {
  Box,
  Icon,
  VStack,
} from "@chakra-ui/react";
import { FaCheck } from "react-icons/fa6";
// @ts-ignore - Will be available after GraphQL codegen
import {
  UpdateAlertStatusDocument,
  UpdateAlertStatusMutation,
  UpdateAlertStatusMutationVariables,
  AlertStatusEnum,
} from "@hydrologiq/gql-experience/graphql";
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient";
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent";
import Figure from "@hydrologiq/shared/figures/generic/Figure";
import { formatAlertMessage } from "@hydrologiq/shared/components/AlertSeverityBadge";

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

interface Alert {
  id: string;
  name: string;
  template_name?: string;
  type?: string;
  severity: string;
  status: string;
  message: string;
  firedAt: string;
}

interface AlertCardProps {
  alert: Alert;
  onStatusUpdate?: (alertId: string, newStatus: string) => void;
  refetchQueries?: string[];
  updateNote?: string;
  isUpdating?: boolean;
  canResolveAlert?: boolean;
}

function AlertCard({
  alert,
  onStatusUpdate,
  refetchQueries = [],
  updateNote = "Updated from alert card",
  isUpdating = false,
  canResolveAlert = true
}: AlertCardProps) {
  const [resolvingAlertId, setResolvingAlertId] = useState<string | null>(null);

  const [updateAlertStatus] = useMutationWithEndpoint<
    UpdateAlertStatusMutation,
    UpdateAlertStatusMutationVariables
  >(UpdateAlertStatusDocument, {
    refetchQueries,
  }, "primary");

  const handleResolveAlert = async (alertId: string, nextStatus: AlertStatusEnum | undefined) => {
    if (!nextStatus) {
      return;
    }
    setResolvingAlertId(alertId);
    try {
      await updateAlertStatus({
        variables: {
          input: {
            id: alertId,
            status: nextStatus,
            note: updateNote,
          },
        },
      });

      // Call the callback if provided
      if (onStatusUpdate) {
        onStatusUpdate(alertId, nextStatus);
      }
    } catch (error) {
      console.error("Failed to resolve alert:", error);
    } finally {
      setResolvingAlertId(null);
    }
  };

  // Determine next status and button text
  let nextStatus: AlertStatusEnum | undefined = undefined;
  let nextStatusText: string | undefined = undefined;

  switch (alert.status) {
    case AlertStatusEnum.New:
      nextStatus = AlertStatusEnum.Acknowledged;
      nextStatusText = "Acknowledge";
      break;
    case AlertStatusEnum.Acknowledged:
      nextStatus = AlertStatusEnum.Completed;
      nextStatusText = "Resolve";
      break;
  }

  const alertType = (alert.type || "status").replace(/_/g, " ");

  const displayName = alert.template_name || alert.name;
  const displayMessage = formatAlertMessage(alert.message);

  const cardHeight = canResolveAlert ? "13.5rem" : "9.5rem";

  return (
    <Box width="100%" flexShrink={0} height={cardHeight}>
      <Figure width="100%" height="100%" minH={cardHeight} heading={displayName} compact>
        <FigureContent.Root compact flex="1" height="100%" justify="space-between">
          <VStack gap={1} align="flex-start" width="100%">
            <FigureContent.Label compact>
              {capitalize(alert.severity)} {alertType} alert
            </FigureContent.Label>
            <FigureContent.Value
              compact
              tooltip={displayMessage}
              fontSize="xs"
              whiteSpace="normal"
              wordBreak="break-word"
              lineHeight="1.35"
            >
              {displayMessage}
            </FigureContent.Value>
          </VStack>

          <VStack gap={1.5} align="flex-start" width="100%">
            <FigureContent.Label compact fontSize="xs" color="gray.500">
              Fired at: {new Date(alert.firedAt).toLocaleString()}
            </FigureContent.Label>
            {nextStatusText && canResolveAlert && (
              <FigureContent.Action
                onClick={() => handleResolveAlert(alert.id, nextStatus)}
                disabled={isUpdating || resolvingAlertId === alert.id}
              >
                {nextStatusText} <Icon as={FaCheck} />
              </FigureContent.Action>
            )}
          </VStack>
        </FigureContent.Root>
      </Figure>
    </Box>
  );
}

export default AlertCard;